import { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useSession } from 'contexts/session-context'
import useRouter from 'hooks/use-router'
import useRedirect from 'hooks/use-redirect'
import Hero from './components/Hero'
import CommunitySection from './components/CommunitySection'
import ViewingPartyBanner from './components/ViewingPartyBanner'
import CampaignVideo from './components/CampaignVideo'
import HowToParticipate from './components/HowToParticipate'
import CalendarSection from './components/CalendarSection'
import SurveyFormSection from './components/SurveyFormSection'
import SuccessSection from './components/SuccessSection'
import ClosedSection from './components/ClosedSection'
import CtaVideoSection from './components/CtaVideoSection'
import ComingNextSection from './components/ComingNextSection'
import { useNeighbarsSurvey } from './hooks/use-neighbars-survey'
import { NEIGHBARS_ASSETS } from './constants'

const SEO_TITLE = 'Neighbars | My Heineken'
const SEO_DESCRIPTION =
  'Registra tu edificio y gana una Viewing Party de la UEFA Champions League en el área común para disfrutar con tus vecinos.'

// Deep link to the survey form: /neighbars#formulario
const FORM_ANCHOR_ID = 'formulario'
const FORM_HASH = `#${FORM_ANCHOR_ID}`
// Fallback anchor for non-logged visitors (the VP banner with the CTA)
const VP_BANNER_ANCHOR_ID = 'viewing-party'

const Neighbars = () => {
  const [{ user }] = useSession()
  const { push, pathname } = useRouter()
  const [, { setRedirect }] = useRedirect()
  const { survey, status, submitAnswers } = useNeighbarsSurvey()

  const isLogged = user !== undefined

  // Persist the campaign page as the post-auth destination before leaving:
  // /callback restores it via internal_redirect (see hooks/use-redirect).
  const goToStart = () => {
    setRedirect(pathname)
    push('/login-manager?tointernal=true')
  }

  const handleViewingPartyCta = () => {
    if (!isLogged) {
      goToStart()
      return
    }
    document.getElementById(FORM_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' })
  }

  // Deep-link handling for /neighbars#formulario:
  // - Logged users: scroll to the survey form (or closure card) once it mounts.
  // - Non-logged users: scroll to the Viewing Party banner (CTA to log in).
  // The intent lives in a ref because the target section mounts after the
  // survey status resolves: consuming the hash on the first run would lose
  // the scroll (the effect re-runs without it once status changes).
  const pendingFormHash = useRef(window.location.hash === FORM_HASH)

  useEffect(() => {
    if (!pendingFormHash.current) return

    const scrollTo = (id: string) => {
      pendingFormHash.current = false
      // Remove the hash so the browser's native anchor resolution doesn't
      // fight with our programmatic scroll. The query string stays: campaign
      // deep links carry UTM params that must survive in the address bar.
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      // Small delay to let the page layout settle (images, lazy sections).
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }, 400)
    }

    if (!isLogged) {
      scrollTo(VP_BANNER_ANCHOR_ID)
      return
    }

    if (status !== 'form' && status !== 'closed') return
    scrollTo(FORM_ANCHOR_ID)
  }, [status, isLogged])

  return (
    <div className="neighbars">
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={`${window.location.origin}${NEIGHBARS_ASSETS}/neighbars-hero_imagen-mobile.webp`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Hero />
      <CommunitySection />
      <ViewingPartyBanner id={VP_BANNER_ANCHOR_ID} onCtaClick={handleViewingPartyCta} />
      <CampaignVideo />
      <HowToParticipate />
      <CalendarSection />
      {isLogged && status === 'form' && survey && (
        <SurveyFormSection survey={survey} onSubmit={submitAnswers} />
      )}
      {isLogged && status === 'answered' && <SuccessSection />}
      {isLogged && status === 'closed' && <ClosedSection id={FORM_ANCHOR_ID} />}
      <CtaVideoSection />
      <ComingNextSection showCta={!isLogged} onCtaClick={goToStart} />
    </div>
  )
}

export default Neighbars
